import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ParamsModal } from '../ParamsModal';
import { CurrentResidenceQuery } from '../../../../graphql/graphql';
import {
  ParamsContainer,
  ParamsItem,
  ParamsItemTitle,
  buttonStyles,
  ParamsSectionContainer
} from './styled';
import { Typography } from '../../../ui/Typography';
import { Button } from '../../../ui/Button';
import { ButtonType } from '../../../../styled/theme/types';
import useScreenSize from '../../../../hooks/useScreenSize';
import { useClient } from '../../../../hooks/useClient';
import { ResidenceSection } from '../ResidenceSection';
import { prettyNumber } from '../../../../utils/helpers';

interface ParamsSectionProps {
  residenceData: Partial<NonNullable<CurrentResidenceQuery['residence']>>;
}

export const ParamsSection: React.FC<ParamsSectionProps> = memo(
  ({ residenceData }) => {
    const { t } = useTranslation();
    const { isSmallOrLargeDesktop } = useScreenSize();
    const isClient = useClient();
    const [isParamsModalOpen, setIsParamsModalOpen] = useState(false);

    const onButtonClick = () => {
      setIsParamsModalOpen(true);
    };

    const {
      deadline,
      buildingClass,
      buildingsCount,
      blocks,
      apartmentsCount,
      liftsPerBlock,
      floorsPerBuilding,
      apartmentsPerFloor,
      technology,
      decoration,
      ceilingHeight,
      windowType,
      layout
    } = residenceData;

    const infoItems = useMemo(
      () => [
        {
          key: 'deadline',
          label: t('residences.deadline'),
          value: deadline
        },
        {
          key: 'buildingClass',
          label: t('residences.class'),
          value: buildingClass?.name
        },
        {
          key: 'buildingsCount',
          label: t('residences.buildings_count_param'),
          value: buildingsCount
        },
        {
          key: 'blocks',
          label: t('residences.blocks_count'),
          value: blocks
        },
        {
          key: 'apartmentsCount',
          label: t('residences.apartments_count'),
          value: prettyNumber(apartmentsCount)
        },
        {
          key: 'liftsPerBlock',
          label: t('residences.lifts_per_block'),
          value: liftsPerBlock
        },
        {
          key: 'floorsPerBuilding',
          label: t('residences.floors_per_building'),
          value: floorsPerBuilding
        },
        {
          key: 'apartmentsPerFloor',
          label: t('residences.apartments_per_floor'),
          value: apartmentsPerFloor
        }
      ],
      [
        t,
        deadline,
        buildingClass,
        buildingsCount,
        blocks,
        apartmentsCount,
        liftsPerBlock,
        floorsPerBuilding,
        apartmentsPerFloor
      ]
    );

    const visibleInfoItems = useMemo(() => {
      const filtered = infoItems.filter(item => item.value);

      if (!isSmallOrLargeDesktop && isClient) {
        return filtered.slice(0, 6);
      }

      return filtered;
    }, [infoItems, isSmallOrLargeDesktop, isClient]);

    const isButtonShown =
      !!residenceData.facilities?.length ||
      technology ||
      decoration ||
      ceilingHeight ||
      windowType ||
      layout;

    return (
      <>
        <ResidenceSection title={t('residences.params_modal_content')}>
          <ParamsSectionContainer>
            <ParamsContainer>
              {visibleInfoItems.map(({ key, label, value }) => (
                <ParamsItem key={key}>
                  <ParamsItemTitle tag="body_1">{label}</ParamsItemTitle>
                  <Typography tag="title_3_desc" $lineToShow={1}>
                    {value}
                  </Typography>
                </ParamsItem>
              ))}
            </ParamsContainer>

            {isButtonShown && (
              <Button
                text={t('residences.all_params')}
                stat="residence-params"
                onClick={onButtonClick}
                state={ButtonType.edit}
                styles={buttonStyles}
              />
            )}
          </ParamsSectionContainer>
        </ResidenceSection>
        {isParamsModalOpen && (
          <ParamsModal
            isOpen={isParamsModalOpen}
            onClose={() => setIsParamsModalOpen(false)}
            residenceData={residenceData}
            infoItems={infoItems}
          />
        )}
      </>
    );
  }
);

ParamsSection.displayName = `ParamsSection`;
